import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import { getLogger } from 'loglevel';

import {
	telemetryFailureReason,
	telemetryOperationName,
	trackClientException,
} from '@apple/lib/app-insights';
import { routeTree } from '@apple/retail/routes.gen';
import { RouterErrorFallback } from '@apple/ui/error/RouterErrorFallback';
import { NotFoundPage } from '@apple/ui/shell/pages/NotFoundPage';
import { NotAuthenticatedError, ServerValidationError } from '@apple/utils/api/errors';
import { getEnv } from '@apple/utils/config/env';

import { profileQueryOptions } from '@apple/features/auth-retail/queries/profile';

const env = getEnv();
const log = getLogger('router');

export type AppleRetailRouter = typeof router;

function isSessionExpired(error: Error): boolean {
	const isNotAuthenticatedError = error instanceof NotAuthenticatedError;
	const isOnAuthRoute =
		router.matchRoute({ to: '/login' }) ||
		router.matchRoute({ to: '/first-login' }) ||
		router.matchRoute({ to: '/reset-password' }) ||
		router.matchRoute({ to: '/session-expired' });
	const isOnLogoutOrSessionExpiredPage =
		router.latestLocation.pathname === '/logout' ||
		router.latestLocation.pathname === '/session-expired';

	return isNotAuthenticatedError && !isOnAuthRoute && !isOnLogoutOrSessionExpiredPage;
}

export const queryClient = new QueryClient({
	queryCache: new QueryCache({
		onError: (error, query) => {
			if (query.queryKey === profileQueryOptions(true).queryKey) {
				return;
			}

			if (isSessionExpired(error)) {
				void router.navigate({
					to: '/session-expired',
					search: { returnUrl: router.latestLocation.pathname },
				});
				return;
			}

			if (env.DEV) {
				log.error('Query Error:', query.options.queryKey, error, query);
			}

			const operation = telemetryOperationName(query.options.queryKey, 'query');
			trackClientException({
				error,
				operationName: `query:${operation}`,
				properties: {
					queryKey: JSON.stringify(query.options.queryKey),
					queryStatus: query.state.status,
					fetchStatus: query.state.fetchStatus,
					fetchFailureReason: telemetryFailureReason(query.state.fetchFailureReason),
				},
				measurements: {
					dataUpdateCount: query.state.dataUpdateCount,
					dataUpdatedAt: query.state.dataUpdatedAt,
					errorUpdateCount: query.state.errorUpdateCount,
					errorUpdatedAt: query.state.errorUpdatedAt,
					fetchFailureCount: query.state.fetchFailureCount,
				},
			});
		},
	}),
	mutationCache: new MutationCache({
		onError: (error, _args, _ctx, mutation) => {
			if (isSessionExpired(error)) {
				return;
			}

			if (env.DEV) {
				log.error('Mutation Error:', mutation.options.mutationKey, error, mutation);
			}

			const operation = telemetryOperationName(mutation.options.mutationKey, 'mutation');
			trackClientException({
				error,
				operationName: `mutation:${operation}`,
				properties: {
					mutationKey: JSON.stringify(mutation.options.mutationKey) ?? operation,
					mutationStatus: mutation.state.status,
					submittedAt: mutation.state.submittedAt,
					failureReason: telemetryFailureReason(mutation.state.failureReason),
				},
				measurements: {
					failureCount: mutation.state.failureCount,
				},
			});
		},
	}),
	defaultOptions: {
		queries: {
			retry: false,
			throwOnError: true,
		},
		mutations: {
			retry: false,
			throwOnError: error => !(error instanceof ServerValidationError),
		},
	},
});

export const router = createRouter({
	routeTree,
	defaultPreload: false,
	defaultNotFoundComponent: NotFoundPage,
	defaultErrorComponent: RouterErrorFallback,
	// Since we're using React Query, we don't want loader calls to ever be stale
	// This will ensure that the loader is always called when the route is preloaded or visited
	// https://tanstack.com/router/latest/docs/framework/react/guide/data-loading#passing-all-loader-events-to-an-external-cache
	defaultPreloadStaleTime: 0,
	//Wrap: ({ children }) => <AppProviders>{children}</AppProviders>,
	// Will most likely be the default in v2
	// https://tanstack.com/router/latest/docs/framework/react/guide/render-optimizations#structural-sharing-with-fine-grained-selectors
	defaultStructuralSharing: true,
	context: {
		queryClient,
		auth: undefined!, // This will be set after we wrap the app in an AuthProvider
		lang: undefined!, // This will be set after we wrap the app in a LangProvider
	},
});
